import { usePlanFeatures } from "@/hooks/usePlanFeatures";
import { useAuthStore } from "@/store/useAuthStore";
import { cn } from "@/utils/cn";
import {
  Building2,
  CalendarCheck,
  ChevronRight,
  FileBarChart,
  LayoutDashboard,
  Lock,
  LogOut,
  Monitor,
  PanelLeft,
  PanelLeftClose,
  Plus,
  Radio,
  Settings,
  Users,
} from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const navItems = [
  {
    label: "Dashboard",
    to: "/dashboard",
    icon: LayoutDashboard,
    featureFlag: undefined,
  },
  {
    label: "Live Monitor",
    to: "/live",
    icon: Radio,
    featureFlag: "liveMonitor",
  },
  { label: "Employees", to: "/employees", icon: Users, featureFlag: undefined },
  {
    label: "Screen Monitor",
    to: "/screenshots",
    icon: Monitor,
    featureFlag: "screenshots",
  },
  {
    label: "Attendance",
    to: "/attendance",
    icon: CalendarCheck,
    featureFlag: undefined,
  },
  {
    label: "Reports",
    to: "/reports",
    icon: FileBarChart,
    featureFlag: "reportsExport",
  },
];

const bottomItems = [
  {
    label: "API Docs",
    to: "/docs",
    icon: FileBarChart,
    featureFlag: undefined as string | undefined,
  },
  {
    label: "Settings",
    to: "/settings",
    icon: Settings,
    featureFlag: undefined as string | undefined,
  },
];

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { user, logout, currentOrgRole, organizations, currentOrgId } =
    useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  // const { hasFeature, isLoading: featuresLoading } = usePlanFeatures();

  const currentOrg = organizations.find((o) => o.orgId === currentOrgId);

  function renderNavItem(item: (typeof navItems)[0]) {
    const Icon = item.icon;
    const isActive =
      location.pathname === item.to ||
      location.pathname.startsWith(item.to + "/");
    // TEST: feature gate bypassed — sidebar items never locked during testing
    // const locked =
    //   !featuresLoading && item.featureFlag
    //     ? !hasFeature(item.featureFlag)
    //     : false;
    const locked = false;

    return (
      <NavLink
        key={item.to}
        to={item.to}
        title={
          locked
            ? "Upgrade to Pro to unlock"
            : collapsed
              ? item.label
              : undefined
        }
        className={cn(
          "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all",
          isActive
            ? "bg-brand-primary/10 text-brand-primary shadow-[0_0_12px_rgba(99,102,241,0.15)]"
            : locked
              ? "cursor-pointer text-text-faint opacity-60 hover:bg-surface-hover hover:opacity-80"
              : "text-text-muted hover:bg-surface-hover hover:text-text-primary",
          collapsed && "justify-center px-0",
        )}
      >
        <Icon
          className={cn(
            "h-5 w-5 flex-shrink-0",
            isActive
              ? "text-brand-primary"
              : locked
                ? "text-text-faint"
                : "text-text-muted",
          )}
        />
        {!collapsed && <span>{item.label}</span>}
        {!collapsed && locked && (
          <Lock className="ml-auto h-3.5 w-3.5 text-text-faint" />
        )}
        {item.to === "/live" && !collapsed && !locked && (
          <span className="ml-auto h-2 w-2 animate-pulse rounded-full bg-signal-success" />
        )}
      </NavLink>
    );
  }

  return (
    <aside
      className={cn(
        "flex h-screen flex-col border-r border-border bg-surface-card transition-all duration-200",
        collapsed ? "w-16" : "w-56",
      )}
    >
      {/* Logo */}
      <div className="flex h-14 items-center border-b border-border-subtle px-4">
        {!collapsed && (
          <span className="text-lg font-bold text-brand-primary">
            Azora Tracker
          </span>
        )}
        <button
          onClick={onToggle}
          className={cn(
            "rounded-md p-1.5 text-text-muted hover:bg-surface-hover hover:text-text-primary",
            collapsed ? "mx-auto" : "ml-auto",
          )}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <PanelLeft className="h-4 w-4" />
          ) : (
            <PanelLeftClose className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-2">{navItems.map(renderNavItem)}</nav>

      {/* Bottom */}
      <div className="border-t border-border-subtle p-2 space-y-1">
        {/* Org switcher */}
        {!collapsed && currentOrg && (
          <button
            onClick={() => navigate("/select-org")}
            className="flex w-full items-center gap-2 rounded-lg bg-surface-hover px-3 py-2 text-left transition-colors hover:bg-surface-raised"
            title="Switch organization"
          >
            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-brand-primary/10">
              <Building2 className="h-4 w-4 text-brand-primary" />
            </div>
            <span className="flex-1 truncate text-xs font-medium text-text-primary">
              {currentOrg.orgName}
            </span>
            <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-text-faint" />
          </button>
        )}
        {collapsed && currentOrg && (
          <button
            onClick={() => navigate("/select-org")}
            className="flex w-full items-center justify-center rounded-lg py-2 text-text-muted hover:bg-surface-hover"
            title={`Switch org (${currentOrg.orgName})`}
          >
            <Building2 className="h-5 w-5" />
          </button>
        )}

        {/* New organization shortcut */}
        {!collapsed && currentOrgRole !== "MANAGER" && (
          <button
            onClick={() => navigate("/create-org")}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-text-faint transition-colors hover:bg-surface-hover hover:text-brand-primary"
          >
            <Plus className="h-3.5 w-3.5" />
            New organization
          </button>
        )}

        {bottomItems.map(renderNavItem)}
        {user && !collapsed && (
          <div className="mt-2 rounded-lg bg-surface-hover px-3 py-2">
            <p className="truncate text-sm font-medium text-text-primary">
              {user.fullName}
            </p>
            <p className="truncate text-xs text-text-muted capitalize">
              {currentOrgRole?.toLowerCase() || "member"}
            </p>
          </div>
        )}
        <button
          onClick={logout}
          title="Logout"
          className={cn(
            "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-text-muted transition-colors hover:bg-signal-error/10 hover:text-signal-error",
            collapsed && "justify-center px-0",
          )}
        >
          <LogOut className="h-5 w-5 flex-shrink-0" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
}
